import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchData } from '../../fetchapi/fetchapi';

// Tenancy guard for the bare /flow-embed/projects/<forkId>/... route. The URL
// addresses a Flow fork by id, so before mounting the studio we ask the
// backend whether the fork's cortex project belongs to one of the caller's
// organizations. The WS proxy enforces the same decision server-side — this
// guard exists so a denied user gets a clear screen instead of a studio that
// silently fails to connect.
export default function FlowEmbedGuard({ children }) {
  const location = useLocation();
  const forkId = location.pathname.match(/flow-embed\/projects\/([0-9a-fA-F]{24})/)?.[1] || null;
  // no fork id in the URL (non-project studio path) → nothing to authorize
  const [state, setState] = useState(forkId ? 'checking' : 'allowed');

  // A SHARE-LINK visitor has no cortex session, so this org check cannot
  // apply to them: it asks "is this fork in one of YOUR organizations", and
  // they have none. They arrive here only because the share page — which
  // already resolved the app against a valid `?k=` capability — iframed this
  // exact fork. Authorization happened there, at the app level, which is the
  // narrower check; re-asking an org question with no org would fail closed
  // and render "No access" behind the share page's loader.
  //
  // Detected from the OUTER page's URL, not from anything the iframe controls:
  // it is same-origin, so window.top is readable, and a cross-origin embed
  // (which cannot be a share page) throws and falls through to the org check.
  const viaShareLink = (() => {
    try {
      const top = window.top?.location;
      return !!top && /\/share\/apps\//.test(top.pathname) && /[?&]k=/.test(top.search);
    } catch { return false; }
  })();

  useEffect(() => {
    if (!forkId || viaShareLink) { setState('allowed'); return; }
    let alive = true;
    setState('checking');
    (async () => {
      try {
        const res = await fetchData(`flow-projects/${forkId}/access`);
        if (alive) setState(res?.allowed ? 'allowed' : 'denied');
      } catch {
        if (alive) setState('denied'); // fail closed
      }
    })();
    return () => { alive = false; };
  }, [forkId, viaShareLink]);

  if (state === 'checking') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: 'var(--page-muted, #64748b)' }}>
        Checking access…
      </div>
    );
  }
  if (state === 'denied') {
    return (
      <div data-testid="flow-embed-denied" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', flexDirection: 'column', gap: 8 }}>
        <div style={{ fontWeight: 600, color: '#b91c1c' }}>No access to this app</div>
        <div style={{ fontSize: 14, color: 'var(--page-muted, #64748b)' }}>
          It belongs to a project outside your organization.
        </div>
      </div>
    );
  }
  return children;
}
